import { DOCUMENT, Injectable, inject } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class JsonLdService {
  private static readonly SCRIPT_ID = 'structured-data';

  private readonly document = inject(DOCUMENT);
  private scriptElement: HTMLScriptElement | undefined;
  private globalData: Record<string, unknown>[] = [];

  public setGlobalStructuredData(data: Record<string, unknown>[]): void {
    this.globalData = data;
    this.updateScript(this.globalData);
  }

  public setStructuredData(
    data: Record<string, unknown> | Record<string, unknown>[],
  ): void {
    const pageData = Array.isArray(data) ? data : [data];
    this.updateScript([...this.globalData, ...pageData]);
  }

  private updateScript(data: Record<string, unknown>[]): void {
    if (!this.scriptElement) {
      this.scriptElement =
        (this.document.querySelector(
          `#${JsonLdService.SCRIPT_ID}`,
        ) as HTMLScriptElement) ?? undefined;
    }
    if (!this.scriptElement) {
      this.scriptElement = this.document.createElement('script');
      this.scriptElement.id = JsonLdService.SCRIPT_ID;
      this.scriptElement.type = 'application/ld+json';
      // Use appendChild instead of append — Domino (SSR DOM) doesn't support append.
      // eslint-disable-next-line unicorn/prefer-dom-node-append
      this.document.head.appendChild(this.scriptElement);
    }
    const jsonLd: Record<string, unknown> =
      data.length === 1
        ? data[0]
        : { '@context': 'https://schema.org', '@graph': data };
    this.scriptElement.textContent = JSON.stringify(jsonLd);
  }

  public static buildFaqSchema(
    items: { question: string; answer: string }[],
  ): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer.replaceAll(/<[^>]*>/g, ''),
        },
      })),
    };
  }

  public static buildWebPageSchema(
    name: string,
    description: string,
    url: string,
    type: string = 'WebPage',
  ): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': type,
      name,
      description,
      url,
    };
  }

  public static buildAggregateRatingSchema(
    ratingValue: number,
    reviewCount: number,
    bestRating: number = 5,
    worstRating: number = 1,
  ): Record<string, unknown> {
    return {
      '@type': 'AggregateRating',
      ratingValue: String(ratingValue),
      reviewCount,
      bestRating,
      worstRating,
    };
  }

  public static buildReviewSchema(
    authorName: string,
    reviewBody: string,
    ratingValue?: number,
  ): Record<string, unknown> {
    const review: Record<string, unknown> = {
      '@type': 'Review',
      author: { '@type': 'Person', name: authorName },
      reviewBody: reviewBody.replaceAll(/<[^>]*>/g, ''),
    };
    if (ratingValue !== undefined) {
      review['reviewRating'] = {
        '@type': 'Rating',
        ratingValue: String(ratingValue),
        bestRating: 5,
        worstRating: 1,
      };
    }
    return review;
  }

  public static buildHowToSchema(
    name: string,
    description: string,
    steps: { name: string; text: string }[],
  ): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name,
      description,
      step: steps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: step.name,
        text: step.text,
      })),
    };
  }

  public static buildSoftwareApplicationSchema(
    name: string,
    description: string,
    url: string,
    options: {
      price?: string;
      priceCurrency?: string;
      aggregateRating?: Record<string, unknown>;
      reviews?: {
        authorName: string;
        reviewBody: string;
        ratingValue?: number;
      }[];
    } = {},
  ): Record<string, unknown> {
    const id = '#capawesome-cloud-app';
    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': id,
      name,
      description,
      url,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Web',
    };
    if (options.price !== undefined && options.priceCurrency !== undefined) {
      schema['offers'] = {
        '@type': 'Offer',
        price: options.price,
        priceCurrency: options.priceCurrency,
      };
    }
    if (options.aggregateRating) {
      schema['aggregateRating'] = options.aggregateRating;
    }
    if (options.reviews?.length) {
      schema['review'] = options.reviews.map(r =>
        JsonLdService.buildReviewSchema(
          r.authorName,
          r.reviewBody,
          r.ratingValue,
        ),
      );
    }
    return schema;
  }
}
